
/* eslint-disable no-undef */
import {useState, useEffect} from 'react';
import {useForm} from 'react-hook-form';
import axios from 'axios';
import validator from 'validator';
import {useLocation} from 'react-router-dom';
import monthListOptions from '../utils/monthListOptions.js';
import yearListOptions from '../utils/yearListOptions.js';
import isValidNationalId from '../utils/isValidNationalId.js';
import getQueryParam from '../utils/getQueryParam.js';

const {
	REACT_APP_API_ENDPOINT = window.location.origin + '/api',
	REACT_APP_API_ROUTE_PREFIX = '',
} = process.env;
const ERR_MSG = 'משהו השתבש. אנא נסו שוב מאוחר יותר.';

function useTransaction(route) {
	const [pciSuccess, setPciSuccess] = useState(false);
	const [errorMessage, setErrorMessage] = useState(null);
	const [loading, setLoading] = useState(false);
	const [paymentCode, setPaymentCode] = useState(null);
	const [paymentAmount, setPaymentAmount] = useState(null);
	const [paymentEmail, setPaymentEmail] = useState();
	const [maxInstallments, setMaxInstallments] = useState(null);
	const [defaultInstallments, setDefaultInstallments] = useState(null);
	const [ccCardData, setCCCardData] = useState([]);
	const [lineCC, setLineCC] = useState(null);
	const [token3Ds, setToken3Ds] = useState(null);
	const [threeDsCollectEmail, setThreeDsCollectEmail] = useState();
	const [threeDsCollectEmailThreshold, setThreeDsCollectEmailThreshold] = useState();
	const form = useForm();

	const webPtoken = getQueryParam('ptoken');
	const webSid = getQueryParam('sid') || '';
	const isMobileApp = Boolean(window.Ptoken);
	const isPlugin = useLocation().pathname === '/plugin';
	const Ptoken = isMobileApp ? window.Ptoken : webPtoken;

	const chosenCC = ccCardData.filter(element => element.Line === lineCC);

	const fields = {
		payerID: {
			label: 'מספר ת.ז (כולל ספרת ביקורת)',
			type: 'tel',
			required: route === 'plugin',
			validate: text => !text || isValidNationalId(text) || 'מספר תעודת זהות אינו תקין',
		},
		ccNumber: {
			label: 'מספר כרטיס האשראי',
			type: 'tel',
			required: true,
			validate: text => validator.isCreditCard(text) || 'מספר כרטיס אשראי אינו תקין',
			savedCardValue: chosenCC?.length ? `${chosenCC[0].First6or2} XXXX XXXX ${chosenCC[0].Last4}` : null,
		},
		ccYear: {
			label: 'שנה',
			size: 6,
			type: 'select',
			required: true,
			options: ['- בחר -', ...yearListOptions()],
		},
		ccMonth: {
			label: 'חודש',
			size: 6,
			type: 'select',
			required: true,
			options: ['- בחר -', ...monthListOptions()],
		},
		cvv: {
			label: 'CVV',
			size: 6,
			type: 'tel',
			required: false,
		},
		paymentAmount: {
			label: 'סה”כ לתשלום:',
			value: `₪${paymentAmount}`,
			size: 6,
			readOnly: true,
			isShown: route === 'plugin' && maxInstallments > 1,
			required: false,
			fromNewLine: true,
		},
		numOfPayments: {
			label: 'מספר תשלומים:',
			type: 'select',
			size: 6,
			options: Array.from({length: Number(maxInstallments)}).fill(0).map((element, i) => i + 1),
			isShown: route === 'plugin' && maxInstallments > 1,
			required: false,
		},
		email: {
			label: 'אנא הזינו פרטי דוא״ל',
			type: 'email',
			required: isEmailRequired(),
			helperText: getEmailHelperText(),
			isShown: threeDsCollectEmail !== 'NO',
			validate: text => !text || validator.isEmail(text) || 'שדה דוא"ל אינו תקין',
		},
	};

	const _api = (path = '/', data = {}, method = 'post') => axios[method](REACT_APP_API_ENDPOINT + REACT_APP_API_ROUTE_PREFIX + path, data, {headers: {sid: webSid}})
		.then(({data}) => data)
		.catch(error => {
			setLoading(false);
			const response = error?.response?.data || {};
			const {errors} = response;
			if (!errors || error.length <= 0) {
				return;
			}

			for (const error of errors) {
				if (fields[error.param]) {
					form.setError(error.param, {msg: error.msg});
				}
			}
		});

	useEffect(() => {
		_api('/pci', {Ptoken}).then(data => {
			if (!data?.data) {
				return;
			}

			setPaymentCode(data.data.PCode);
			window.PCode = data.data.PCode;
			if (data.data.PaymentAmount) {
				setPaymentAmount(data.data.PaymentAmount);
			}

			setMaxInstallments(data.data.MaxInstallments);
			setDefaultInstallments(data.data.DefaultInstallments);
			setCCCardData(data.data.CCCardData ?? []);
			setToken3Ds(data.data.token3Ds);
			setThreeDsCollectEmail(data.data.threeDsCollectEmail);
			setThreeDsCollectEmailThreshold(data.data.threeDsCollectEmailThreshold);
			if (data.data.EmailThreeDS) {
				setPaymentEmail(data.data.EmailThreeDS);
			}
		});
	}, [null]);

	useEffect(() => {
		for (const name of Object.keys(fields)) {
			form.setValue(name, '');
			form.clearErrors();
			if (name !== 'paymentAmount') {
				form.register(name, {
					required: lineCC ? false : fields[name].required,
					validate: lineCC ? false : fields[name].validate,
				});
			}
		}
	}, [form.register, lineCC]);

	useEffect(() => {
		const required = isEmailRequired();
		form.register('email', {required});
	}, [paymentAmount, threeDsCollectEmail, threeDsCollectEmailThreshold]);

	useEffect(() => {
		form.setValue('email', paymentEmail);
	}, [paymentEmail]);

	useEffect(() => {
		if (maxInstallments <= 1) {
			return;
		}

		form.setValue('numOfPayments', defaultInstallments);
	}, [defaultInstallments, maxInstallments, form.setValue]);

	useEffect(() => {
		window.socket.on('3ds:challenge-success', token3Ds => {
			console.log('3ds:challenge-success', token3Ds);
			setToken3Ds(token3Ds);
			window.token3Ds = token3Ds;
			setTimeout(form.handleSubmit(_onSubmit), 500);

			const appContainer = document.querySelectorAll('.App')[0];
			appContainer.style.display = 'block';

			document.querySelector('#threeDSCReqIframe').remove();
		});

		window.socket.on('3ds:challenge-failed', () => {
			setErrorMessage(ERR_MSG);
			setLoading(false);
			const appContainer = document.querySelectorAll('.App')[0];
			appContainer.style.display = 'block';
			document.querySelector('#threeDSCReqIframe').remove();
		});
	}, [null]);

	const _on3Dchallange = data => {
		console.log('_on3Dchallange');
		const appContainer = document.querySelectorAll('.App')[0];
		appContainer.style.display = 'none';

		const {data: {gamaTransactionId}} = data || {};
		const socket3dSubscribe = () => {
			window.socket.emit('3ds:subscribe', gamaTransactionId, error => {
				if (error) {
					console.error(error);
				}
			});
		};

		socket3dSubscribe();
		window.socket.io.on('reconnect', socket3dSubscribe);

		window.nca3DSWebSDK.createIframeAndInit3DSMethod(data.data.threeDSMethodURL, data.data.threeDSMethodData, 'threeDSMethodIFrameContainer', window.document.body, () => {
			console.log('createIframeAndInit3DSMethod');
		});
		window.nca3DSWebSDK.createIFrameAndInit3DSChallengeRequest(data.data.challengeAcsUrl, data.data.base64EncodedChallengeRequest, '05', 'threeDSCReqIFrame', window.document.body, () => {
			console.log('here-3d');
		});
	};

	const _onSuccess = data => {
		if (!isMobileApp && window.parent) {
			window.parent.postMessage(data, '*');
		} else {
			window.ReactNativeWebView.postMessage(JSON.stringify(data));
		}

		setPciSuccess(true);
	};

	useEffect(() => {
		if (isMobileApp) {
			window.ReactNativeWebView.postMessage(document.body.scrollHeight + 20);
		} else if (isPlugin) {
			window.parent.postMessage({
				action: '_pci_web_height',
				payload: {
					height: document.body.scrollHeight + 20,
				},
			}, '*');
		}
	}, [document.body.scrollHeight, Ptoken, ccCardData]);

	const selectCC = line => {
		line = (line === lineCC) ? null : line;
		setLineCC(line);
	};

	const stepBack = () => isMobileApp
		? window.ReactNativeWebView.postMessage('stepBack')
		: window.parent.postMessage('stepBack', '*');

	const _onSubmit = ({savePayerCreditData, ...data}) => {
		setErrorMessage(null);
		setLoading(true);
		data = {
			...data,
			PCode: paymentCode || window.PCode,
			LineCC: lineCC,
			savePayerCreditData: Boolean(savePayerCreditData),
			token3Ds: token3Ds || window.token3Ds,
		};

		const urlPath = lineCC ? '/pci/credit-card/line' : '/pci/credit-card';

		_api(urlPath, data).then(data => {
			if (data && data.success) {
				if (data?.data?.is3DsEnabled) {
					_on3Dchallange(data);
				} else {
					_onSuccess(data);
				}
			} else {
				setErrorMessage(ERR_MSG);
				setLoading(false);
			}
		});
	};

	return {
		PCode: paymentCode,
		CCCardData: ccCardData,
		LineCC: lineCC,
		selectCC,
		fields,
		watch: form.watch,
		errors: form.errors,
		errMsg: errorMessage,
		loading,
		setValue: form.setValue,
		trigger: form.trigger,
		stepBack,
		isMobileApp,
		pciSuccess,
		onSubmit: form.handleSubmit(_onSubmit),
		setPaymentAmount,
		setPaymentEmail,
	};

	function isEmailRequired() {
		if (threeDsCollectEmail === 'MANDATORY') {
			return true;
		}

		return (threeDsCollectEmail === 'ENFORCE_ABOVE_THRESHOLD' && Number(paymentAmount) > Number(threeDsCollectEmailThreshold));
	}

	function getEmailHelperText() {
		if (!['ENFORCE_ABOVE_THRESHOLD', 'RECOMMEND_ABOVE_THRESHOLD'].includes(threeDsCollectEmail)) {
			return '';
		}

		return `${threeDsCollectEmail === 'ENFORCE_ABOVE_THRESHOLD' ? 'חובה' : 'מומלץ'} להזין דוא״ל בעסקאות מעל ${threeDsCollectEmailThreshold ?? ''} ש״ח להגדלת מהימנות העסקה`;
	}
}

export default useTransaction;
